import React, {useMemo, useState} from 'react';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Tag} from "primereact/tag";
import {useGetDatasetsInfo} from "api/api-hooks/datasets-hooks";
import CopyTextWithButton from "../copy-text-with-button/CopyTextWithButton";
import {DatasetInfo} from "api/model/dataset/DatasetInfo";
import {MultiSelect} from "primereact/multiselect";

type DatasetsPropertiesTableProps = {
    identifiers: string[]
}

type DatasetInfoWithID = DatasetInfo & {
    id: string[]
}

type BodyMapperProps = {
    id: (rowData: DatasetInfo) => React.ReactNode;
    groupId: (rowData: DatasetInfo) => React.ReactNode;
    categoryId: (rowData: DatasetInfo) => React.ReactNode;
    datasetClass: (rowData: DatasetInfo) => React.ReactNode;
    tags: (rowData: DatasetInfo) => React.ReactNode;
};

const COLUMN_OPTIONS = [
    {label: "ID", value: "id"},
    {label: "Group", value: "groupId"},
    {label: "Data type", value: "dataType"},
    {label: "Unit", value: "unit"},
    {label: "Category", value: "categoryId"},
    {label: "Dataset class", value: "datasetClass"},
    {label: "Access type", value: "accessType"},
    {label: "Decimal places", value: "decimalPlaces"},
    {label: "Tags", value: "tags"}
]

const DatasetsPropertiesTable = ({identifiers}: DatasetsPropertiesTableProps) => {
    const {data, isLoading, error} = useGetDatasetsInfo(identifiers)


    const [selectedColumns, setSelectedColumns] = useState<string[]>(['id', 'groupId', 'dataType', 'accessType'])
    const formattedData = useMemo(() => (
        data ? data.map(e => ({ ...e, id: e.identifier ? e.identifier.split(":") : [] })) : []
    ), [data]);

    const idBody = (rowData: DatasetInfoWithID) => {
        return <CopyTextWithButton
            text={rowData.id[0]}
            copyableText={rowData.identifier!}
        />
    }

    const groupBody = (rowData: DatasetInfo) => {
        if (rowData.groupTitle && rowData.groupId) {
            return `${rowData.groupTitle} [${rowData.groupId}]`;
        } else if (rowData.groupTitle) {
            return rowData.groupTitle;
        } else if (rowData.groupId) {
            return `[${rowData.groupId}]`;
        } else {
            return "";
        }
    };

    const categoryBody = (rowData: DatasetInfo) => {
        if (rowData.categoryTitle && rowData.categoryId) {
            return `${rowData.categoryTitle} [${rowData.categoryId}]`;
        } else if (rowData.categoryTitle) {
            return rowData.categoryTitle;
        } else if (rowData.categoryId) {
            return `[${rowData.categoryId}]`;
        } else {
            return "";
        }
    }

    const datasetClassBody = (rowData: DatasetInfo) => {
        return <Tag value={rowData.datasetClass}></Tag>
    }

    const tagsBody = (rowData: DatasetInfo) => {
        return <div className={"flex gap-1 flex-wrap"}>
            {rowData.tags.map((e, index) => <Tag value={e} key={index}></Tag>)}
        </div>
    }

    if (error) {
        return <div>Failed to fetch dataset information: {error.message}</div>;
    }

    const bodyMapper: BodyMapperProps = {
        id: idBody,
        groupId: groupBody,
        categoryId: categoryBody,
        datasetClass: datasetClassBody,
        tags: tagsBody,
    };

    return <div className="flex flex-column gap-1 relative">
        <div className="flex flex-column gap-3 mt-1 align-items-end">
            <MultiSelect value={selectedColumns} options={COLUMN_OPTIONS} onChange={(e) => setSelectedColumns(e.value)}
                         maxSelectedLabels={2}
            />
        </div>
        <DataTable value={formattedData} loading={isLoading} size={"small"}>
            <Column header={"Title"} field={"title"}></Column>
            {selectedColumns.map((column) => {
                const header = COLUMN_OPTIONS.find(e => e.value === column)?.label
                return <Column key={column} header={header} field={column} body={bodyMapper[column]}
                />
            })}
        </DataTable>
    </div>
}

export default DatasetsPropertiesTable;
